import * as moment from 'moment';

/**
 * Values the ACH disclosure form (case_form_NACHA) reads back to the debtor.
 * They only exist in the payment page's reactive form, so they are collected
 * here and handed to CustomFormPage, which substitutes them into the form
 * definition before Form.io renders it.
 */
export interface NachaVariableSource {
  accountType?: string;
  routingNumber?: string;
  accountNumber?: string;
  amount?: string | number;
  currencySymbol?: string;
  paymentDate?: string;
  accountName?: string;
  // Recurring plans only. scheduleHtml is the arrangement schedule table the
  // page already shows under "Payment Schedule" - it comes from the server's
  // previewArrangementPlan call, so it is the authoritative schedule and is
  // reused verbatim. The rest are the values the page sends to the gateway,
  // used only to rebuild an equivalent table if that markup is not available.
  scheduleHtml?: string;
  numberOfPayments?: string | number;
  frequency?: string | number;
}

/**
 * Build the [[token]] map for the disclosure form. Every ACH page produces the
 * same token names so one form definition serves all four gateways/schedules.
 */
export function buildNachaVariables(source: NachaVariableSource): { [key: string]: string } {
  const schedule = buildScheduleTable(source);
  return {
    account_type: formatAccountType(source.accountType),
    routing_number: source.routingNumber ? String(source.routingNumber) : '',
    // Never expose the full account number in the disclosure wording.
    account_last4: lastFour(source.accountNumber),
    payment_amount: formatAmount(source.amount, source.currencySymbol),
    payment_date: formatDate(source.paymentDate),
    account_name: (source.accountName || '').trim(),
    // The arrangement schedule, under both names a form author is likely to
    // reach for. Same value - the table.
    schedule_summary: schedule,
    payment_schedule: schedule,
    // The same schedule as one sentence, for wording that reads inline.
    schedule_summary_text: buildScheduleSummary(source),
  };
}

/**
 * Every instalment of the arrangement as a table, for the disclosure to list.
 * Prefers the schedule table the page is already showing the debtor under
 * "Payment Schedule": that markup comes from the server's previewArrangementPlan
 * call, so it is the authoritative schedule and is reused verbatim rather than
 * recomputed. Empty for a single payment, so one helper serves both disclosures.
 */
function buildScheduleTable(source: NachaVariableSource): string {
  const provided = (source.scheduleHtml || '').trim();
  if (provided) {
    return normaliseScheduleTable(provided);
  }
  const instalments = buildInstalments(source);
  if (!instalments.length) {
    return '';
  }
  const symbol = source.currencySymbol || '';
  const rows = instalments.map((instalment) => (
    '<tr><td>' + instalment.date + '</td>' +
    '<td>' + symbol + instalment.amount.toFixed(2) + '</td></tr>'
  )).join('');
  return '<table class="schedule-summary-table">' +
    '<tr><td><strong>Date Received</strong></td><td><strong>Amount</strong></td></tr>' +
    rows +
    '</table>';
}

/**
 * Restyle the page's schedule table for the disclosure. The payment screen sizes
 * it as a narrow centred grid column ("col-sm-6 col-md-3" plus inline centring),
 * which reads as a stray floating block in a page of body text - so the layout
 * classes are dropped in favour of one class the disclosure stylesheet owns.
 * Amounts are padded to two decimals (the schedule API returns "17.5"); the
 * numbers themselves are untouched.
 */
function normaliseScheduleTable(table: string): string {
  // Drop the payment screen's own classes - the grid sizing on the table and the
  // Bootstrap helpers on the cells, whose !important rules would otherwise beat
  // the stylesheet - leaving one class for the disclosure styles to own.
  const body = table
    .replace(/^\s*<table\b[^>]*>/, '')
    .replace(/\sclass="[^"]*"/g, '');
  // A cell holding a number, optionally behind a currency symbol. Dates such as
  // "19-08-2026" cannot match: the digits are not followed by the end of the cell.
  const padded = body.replace(
    />\s*([^0-9<>]{0,12}?)([0-9]+(?:,[0-9]{3})*)(?:\.([0-9]{1,2}))?\s*</g,
    (match, prefix, whole, fraction) => '>' + prefix + whole + '.' + ((fraction || '') + '00').slice(0, 2) + '<'
  );
  return '<table class="schedule-summary-table">' + padded;
}

/**
 * One-line version of the same schedule, for wording that reads inline rather
 * than showing a table.
 */
function buildScheduleSummary(source: NachaVariableSource): string {
  const instalments = buildInstalments(source);
  if (!instalments.length) {
    return '';
  }
  const symbol = source.currencySymbol || '';
  const label = frequencyLabel(source.frequency);
  const amount = symbol + instalments[0].amount.toFixed(2);
  if (instalments.length === 1) {
    return 'as a single payment of ' + amount + ' on ' + instalments[0].date;
  }
  return instalments.length + ' payments of ' + amount + ' ' + label +
    ', starting ' + instalments[0].date +
    ' and ending ' + instalments[instalments.length - 1].date;
}

/**
 * Expand the schedule into one entry per instalment. Mirrors the page's own
 * getScheduleEnds() stepping so the dates listed are the dates requested.
 */
function buildInstalments(source: NachaVariableSource): { date: string, amount: number }[] {
  const count = Math.floor(Number(source.numberOfPayments));
  const perPayment = parseFloat(String(source.amount ?? '').replace(/[^0-9.\-]/g, ''));
  const start = source.paymentDate ? moment(source.paymentDate) : moment();
  if (!count || count < 1 || isNaN(perPayment) || !start.isValid()) {
    return [];
  }
  const instalments: { date: string, amount: number }[] = [];
  for (let index = 0; index < count; index++) {
    instalments.push({
      date: stepDate(start, index, source.frequency).format('DD-MM-YYYY'),
      amount: perPayment,
    });
  }
  return instalments;
}

function stepDate(start: any, steps: number, frequency: any) {
  const date = start.clone();
  switch (String(frequency)) {
    case '3': return date.add(steps, 'weeks');       // weekly
    case '6': return date.add(steps * 2, 'weeks');   // biweekly
    case '4': return date.add(steps, 'months');      // monthly
    default:  return date.add(steps, 'months');
  }
}

function frequencyLabel(frequency: any) {
  switch (String(frequency)) {
    case '3': return 'weekly';
    case '6': return 'every two weeks';
    case '4': return 'monthly';
    default:  return 'monthly';
  }
}

function formatAccountType(accountType) {
  const value = String(accountType || '').trim();
  if (!value) {
    return '';
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function lastFour(accountNumber) {
  const digits = String(accountNumber || '').replace(/\D/g, '');
  return digits.slice(-4);
}

function formatAmount(amount, currencySymbol) {
  if (amount === null || amount === undefined || amount === '') {
    return '';
  }
  // The pages hold the amount either raw ("1") or already prefixed with the
  // currency symbol for a disabled input ("$1"). Strip anything non-numeric so
  // the symbol is never doubled up.
  const numeric = parseFloat(String(amount).replace(/[^0-9.\-]/g, ''));
  if (isNaN(numeric)) {
    return '';
  }
  return (currencySymbol || '') + numeric.toFixed(2);
}

function formatDate(paymentDate) {
  const date = paymentDate ? moment(paymentDate) : moment();
  // US format - NACHA authorisations are a US instrument.
  return date.isValid() ? date.format('MM/DD/YYYY') : '';
}
